import React, { useState, useEffect } from "react";
import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, useTheme, Box, Typography, Divider } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  Description as ConsentIcon,
  Assignment as PurposeIcon,
  Storage as DataIcon,
  Notifications as NoticeIcon,
  TrackChanges as RequestIcon,
  Help as SupportIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES, ROUTE_METADATA } from "../../config/routes";

interface PortalSidebarProps {
  drawerWidth: number;
  open: boolean;
  onClose: () => void;
}

const PortalSidebar: React.FC<PortalSidebarProps> = ({ drawerWidth, open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Navigation items for DP Portal
  const navigationItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: ROUTES.DASHBOARD,
      icon: <DashboardIcon />,
    },
    {
      id: "consents",
      label: "Forms",
      path: ROUTES.CONSENTS,
      icon: <ConsentIcon />,
    },
    {
      id: "purposes",
      label: "Purposes",
      path: ROUTES.PURPOSES,
      icon: <PurposeIcon />,
    },
    {
      id: "data",
      label: "Data",
      path: ROUTES.DATA,
      icon: <DataIcon />,
    },
    {
      id: "notices",
      label: "Notices",
      path: ROUTES.NOTICES,
      icon: <NoticeIcon />,
    },
    {
      id: "requests",
      label: "Requests",
      path: ROUTES.REQUESTS,
      icon: <RequestIcon />,
    },
    {
      id: "support",
      label: "Support",
      path: ROUTES.SUPPORT,
      icon: <SupportIcon />,
    },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose(); // Close sidebar on mobile after navigation
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  return (
    <Drawer
      variant="temporary"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        display: { xs: "block", md: "none" },
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          width: drawerWidth,
          bgcolor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Navigation
        </Typography>
      </Box>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={isActiveRoute(item.path)}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main + "20",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main + "30",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActiveRoute(item.path) ? theme.palette.primary.main : theme.palette.text.secondary,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontWeight: isActiveRoute(item.path) ? 600 : 400,
                    color: isActiveRoute(item.path) ? theme.palette.primary.main : theme.palette.text.primary,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export default PortalSidebar;
